import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Sidebar } from './Sidebar';
import { useIsMobile } from '../hooks/useMediaQuery';
import { ForcePasswordChangeModal } from './ForcePasswordChangeModal';

interface LayoutProps {
  children: React.ReactNode;
  title: string;
  showBackToDashboard?: boolean;
}

export function Layout({ children, title, showBackToDashboard = true }: LayoutProps) {
  const { user, logout, forcePasswordChange } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Initialize sidebar state from localStorage, default to open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = localStorage.getItem('sidebarOpen');
    if (saved !== null) return JSON.parse(saved);
    return !isMobile; // Default: open on desktop, closed on mobile
  });

  // Save sidebar preference to localStorage (only on desktop)
  const handleSidebarToggle = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    if (!isMobile) {
      localStorage.setItem('sidebarOpen', JSON.stringify(newState));
    }
  };

  // Only auto-close on mobile, don't override user preference on desktop
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={handleSidebarToggle}
        isAdmin={user?.role === 'ADMIN'}
      />

      {/* Main content area */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'}`}>
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <div className={`flex items-center gap-2 lg:gap-4 ${sidebarOpen ? 'lg:ml-0' : 'lg:ml-8'}`}>
              {/* Hamburger menu for mobile */}
              <button
                onClick={handleSidebarToggle}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                aria-label="Toggle menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              {showBackToDashboard && title !== 'Dashboard' && (
                <button
                  onClick={() => navigate('/')}
                  className="text-base lg:text-lg text-gray-500 hover:text-gray-700"
                >
                  &larr; Dashboard
                </button>
              )}
              <h1 className="text-xl lg:text-2xl font-bold text-gray-900">{title}</h1>
            </div>
            <div className="flex items-center gap-4 lg:gap-6">
              <button
                onClick={() => navigate('/profile')}
                className="text-base lg:text-lg text-gray-700 hover:text-gray-900 font-medium"
              >
                {user?.name || user?.email}
                {user?.role === 'ADMIN' && (
                  <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                    Admin
                  </span>
                )}
              </button>
              <button
                onClick={handleLogout}
                className="text-base text-gray-600 hover:text-gray-800"
              >
                Esci
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          {children}
        </main>
      </div>

      {/* Force password change modal */}
      {forcePasswordChange && <ForcePasswordChangeModal />}
    </div>
  );
}
